import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowUpTrayIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ecuBinaryRejection } from "../utils/ecuFileGuard";
import { Alert, Button, CONTROL, Field, Input, Modal, Textarea } from "./ui";
import { createEcuTicket, getEcuTicketOptions, SUBJECT_TYPES, ticketErrorMessage } from "../utils/ecuTicketsApi";

/**
 * The Automatic support request form, opened from the Automatic page for the file the customer
 * uploaded there. That file is always the request's original file, and the brand + ECU come from
 * its identification — the customer only picks what to ask for (and adds the modified file for a
 * bug report). Brand and ECU stay editable only when the file could not be identified.
 */
const EcuTicketRequestModal = ({
  file,
  brand: identifiedBrand,
  ecu: identifiedEcu,
  onSolutions = [],
  onClose,
  onCreated,
}) => {
  const identified = Boolean(identifiedBrand && identifiedEcu);
  const [subjectType, setSubjectType] = useState(SUBJECT_TYPES[0].value);
  const [brand, setBrand] = useState(identifiedBrand || "");
  const [ecu, setEcu] = useState(identifiedEcu || "");
  const [options, setOptions] = useState({ modules: [], tunings: [] });
  const [items, setItems] = useState([]);
  const [description, setDescription] = useState("");
  const [modified, setModified] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const subject = SUBJECT_TYPES.find((s) => s.value === subjectType) || SUBJECT_TYPES[0];
  const isBug = subjectType === "BUG";
  // A bug report picks from the solutions the check found ON for this file (optional — which one
  // misbehaves); a new-solution request picks from what Ecumatic can add for this brand + ECU.
  const offered = isBug ? onSolutions : options.modules || [];

  // The solutions (modules) a New Solution Request can ask for, for this brand + ECU.
  useEffect(() => {
    const b = brand.trim();
    const e = ecu.trim();
    if (!b || !e) {
      setOptions({ modules: [], tunings: [] });
      return undefined;
    }
    let active = true;
    const timer = setTimeout(
      () => {
        getEcuTicketOptions(b, e)
          .then((data) => active && setOptions(data || { modules: [], tunings: [] }))
          .catch(() => active && setOptions({ modules: [], tunings: [] }));
      },
      identified ? 0 : 400
    );
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [brand, ecu, identified]);

  // Switching subject starts its picks over; only a bug report carries a modified file, so it is
  // dropped too (it would otherwise be sent with a new-solution request).
  useEffect(() => {
    setItems([]);
    if (subjectType !== "BUG") {
      setModified(null);
    }
  }, [subjectType]);

  // Required fields left empty are shown in red once the customer tries to send (no message —
  // the red field says what is missing), and turn back as soon as they are filled.
  const [showMissing, setShowMissing] = useState(false);
  // Each failed send shakes the empty fields once; re-armed on every attempt.
  const [shaking, setShaking] = useState(false);
  const shakeTimer = useRef(null);
  useEffect(() => () => clearTimeout(shakeTimer.current), []);
  const shake = () => {
    setShaking(false);
    clearTimeout(shakeTimer.current);
    // Next frame, so a shake already playing restarts instead of being ignored.
    requestAnimationFrame(() => {
      setShaking(true);
      shakeTimer.current = setTimeout(() => setShaking(false), 450);
    });
  };
  const missing = {
    brand: !brand.trim(),
    ecu: !ecu.trim(),
    modified: isBug && !modified,
    description: !!subject.needsDescription && !description.trim(),
    items: !!subject.needsItems && items.length === 0,
  };
  const invalid = (field) => showMissing && missing[field];
  const invalidClass = (field) => (invalid(field) ? `${INVALID} ${shaking ? "animate-shake-once" : ""}` : "");

  const toggleItem = (name) =>
    setItems((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!file) return setError("Upload the file this request is about first.");
    if (Object.values(missing).some(Boolean)) {
      setShowMissing(true);
      shake();
      return;
    }
    setSaving(true);
    try {
      const created = await createEcuTicket({
        subjectType,
        brand: brand.trim(),
        ecu: ecu.trim(),
        description,
        items,
        original: file,
        modified: isBug ? modified : null,
      });
      onCreated(created);
    } catch (err) {
      setError(await ticketErrorMessage(err, "Unable to open the request."));
    } finally {
      setSaving(false);
    }
  };

  // Portalled to <body> so the overlay always covers the whole window above the sticky header
  // and the sidebar, whatever the Automatic page around it does; the form scrolls inside the
  // dialog so a long request (items + description + files) is never cut off at the bottom.
  return createPortal(
    <Modal title="Automatic support request" onClose={onClose} size="max-w-2xl" tone="plain">
      <form onSubmit={submit} className="-mr-2 max-h-[calc(100vh-12rem)] space-y-4 overflow-y-auto pr-2">
        <Field label="Original file">
          <span
            className={`flex h-11 items-center gap-2 rounded-[12px] border px-4 text-sm ${CONTROL}`}
            title={file?.name}
          >
            <DocumentTextIcon className="h-4 w-4 shrink-0 text-red-500 dark:text-orange-400" />
            <span className="truncate">{file?.name}</span>
          </span>
        </Field>
        {isBug ? (
          <Field label="Modified file" required hint="The file that shows the problem.">
            <ModifiedFilePicker
              file={modified}
              original={file}
              onChange={setModified}
              invalid={invalid("modified")}
              shaking={shaking}
            />
          </Field>
        ) : null}
        <Field label="Subject" required>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Subject">
            {SUBJECT_TYPES.map((s) => {
              const active = s.value === subjectType;
              return (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSubjectType(s.value)}
                  className={`h-11 rounded-[12px] border px-4 text-sm font-semibold transition ${
                    active
                      ? "border-red-500 bg-red-500/10 text-red-600 dark:border-orange-400 dark:text-orange-300"
                      : `${CONTROL} hover:border-red-400/60`
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Brand" required hint={identified ? "From the identified file" : undefined}>
            <Input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              readOnly={identified}
              placeholder="e.g. VAG"
              className={invalidClass("brand")}
            />
          </Field>
          <Field label="ECU" required hint={identified ? "From the identified file" : undefined}>
            <Input
              value={ecu}
              onChange={(e) => setEcu(e.target.value)}
              readOnly={identified}
              placeholder="e.g. EDC17CP54"
              className={invalidClass("ecu")}
            />
          </Field>
        </div>
        {subject.needsItems || (isBug && offered.length > 0) ? (
          <Field
            label={isBug ? "Solution with the problem" : "Solutions"}
            required={!!subject.needsItems}
            hint={
              isBug
                ? "Optional — the solutions that are ON for this file."
                : offered.length === 0
                ? "Nothing to request for this brand and ECU yet."
                : undefined
            }
          >
            <div
              className={`flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-[12px] ${
                invalid("items")
                  ? `border border-rose-500 p-2 ring-4 ring-rose-500/10 ${shaking ? "animate-shake-once" : ""}`
                  : ""
              }`}
            >
              {offered.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleItem(name)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    items.includes(name)
                      ? "border-red-500 bg-red-500/10 text-red-600 dark:text-orange-300"
                      : "border-gray-300 text-gray-600 hover:border-red-400 dark:border-[#3a3a3d] dark:text-slate-300"
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </Field>
        ) : null}
        <Field label="Description" required={!!subject.needsDescription}>
          <Textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={invalidClass("description")}
          />
        </Field>
        <Alert tone="error">{error}</Alert>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || !file}>
            {saving ? "Sending…" : "Send request"}
          </Button>
        </div>
      </form>
    </Modal>,
    document.body
  );
};

// Red outline for a required field the customer left empty (overrides the control's own border).
const INVALID = "!border-rose-500 ring-4 ring-rose-500/10";

const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

/**
 * The modified-file picker: a drop zone (click or drag & drop) that becomes a file chip once a
 * file is chosen, with Change / Remove. A file is checked like any Automatic upload (only a raw
 * ECU binary is accepted) and must not be the original file again.
 */
const ModifiedFilePicker = ({ file, original, onChange, invalid = false, shaking = false }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const pick = async (next) => {
    setError("");
    if (!next) return;
    if (original && next.name === original.name && next.size === original.size) {
      setError("That is the original file — choose the modified one.");
      return;
    }
    setChecking(true);
    try {
      const rejection = await ecuBinaryRejection(next);
      if (rejection) {
        setError(rejection);
        return;
      }
      onChange(next);
    } finally {
      setChecking(false);
    }
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        onChange={(e) => {
          const picked = e.target.files?.[0] || null;
          // Clear so picking the same file again after a rejection still fires onChange.
          e.target.value = "";
          pick(picked);
        }}
      />
      {file ? (
        <div className={`flex h-11 items-center gap-2 rounded-[12px] border px-4 text-sm ${CONTROL}`}>
          <DocumentTextIcon className="h-4 w-4 shrink-0 text-red-500 dark:text-orange-400" />
          <span className="min-w-0 flex-1 truncate" title={file.name}>
            {file.name}
          </span>
          <span className="shrink-0 text-xs text-gray-400">{formatBytes(file.size)}</span>
          <button
            type="button"
            onClick={openPicker}
            className="shrink-0 rounded-full px-2 py-1 text-xs font-semibold text-gray-500 transition hover:text-red-500 dark:text-slate-400 dark:hover:text-orange-400"
          >
            Change
          </button>
          <button
            type="button"
            onClick={() => {
              setError("");
              onChange(null);
            }}
            aria-label="Remove the modified file"
            className="shrink-0 rounded-full p-1 text-gray-400 transition hover:bg-red-500/10 hover:text-red-500"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            pick(e.dataTransfer.files?.[0] || null);
          }}
          className={`flex w-full flex-col items-center justify-center gap-1 rounded-[12px] border-2 border-dashed px-4 py-5 text-sm transition ${
            dragging
              ? "border-red-400 bg-red-500/5 text-red-600 dark:text-orange-300"
              : invalid
              ? `border-rose-500 bg-rose-500/5 text-rose-600 ring-4 ring-rose-500/10 dark:text-rose-400 ${
                  shaking ? "animate-shake-once" : ""
                }`
              : "border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400 dark:hover:text-orange-300"
          }`}
        >
          <ArrowUpTrayIcon className="h-5 w-5" />
          <span className="font-semibold">{checking ? "Checking the file…" : "Click or drop the modified file"}</span>
          <span className="text-xs text-gray-400 dark:text-slate-500">The ECU file after your change</span>
        </button>
      )}
      {error ? <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">{error}</p> : null}
    </div>
  );
};

export default EcuTicketRequestModal;
